import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Delete, Add, Block } from '@mui/icons-material';

function DisallowedList() {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchDisallowedEmails();
  }, []);

  const fetchDisallowedEmails = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/disallowed-emails`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEmails(data.emails || []);
      } else {
        setError('Failed to load disallowed emails');
      }
    } catch (err) {
      setError(`Error loading disallowed emails: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    // Basic email validation
    if (!newEmail.endsWith('@nkpmedical.com')) {
      setError('Only @nkpmedical.com emails can be added');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/disallowed-emails`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: newEmail.toLowerCase().trim() })
      });

      if (response.ok) {
        const data = await response.json();
        setEmails(data.emails);
        setNewEmail('');
        setSuccess(`Email ${newEmail} has been added to the disallowed list`);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to add email');
      }
    } catch (err) {
      setError(`Error adding email: ${err.message}`);
    }
  };

  const handleRemoveEmail = async (email) => {
    setError('');
    setSuccess('');

    if (!window.confirm(`Are you sure you want to remove ${email} from the disallowed list?`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/disallowed-emails/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEmails(data.emails);
        setSuccess(`Email ${email} has been removed from the disallowed list`);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to remove email');
      }
    } catch (err) {
      setError(`Error removing email: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Block sx={{ fontSize: 40, color: 'error.main' }} />
        <Box>
          <Typography variant="h4" gutterBottom>
            Disallowed Email List
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage NKP Medical emails that are not allowed to access this platform
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Add Email Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add Email to Disallowed List
        </Typography>
        <form onSubmit={handleAddEmail}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label="Email Address"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="user@nkpmedical.com"
              helperText="Only @nkpmedical.com emails can be added"
              type="email"
            />
            <Button
              type="submit"
              variant="contained"
              startIcon={<Add />}
              sx={{ mt: 0.5, minWidth: 120 }}
            >
              Add
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Disallowed Emails List */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Disallowed Emails ({emails.length})
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {emails.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No disallowed emails yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Add an email above to prevent users from accessing the platform
            </Typography>
          </Box>
        ) : (
          <List>
            {emails.map((email, index) => (
              <React.Fragment key={email}>
                <ListItem>
                  <ListItemText
                    primary={email}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveEmail(email)}
                      color="error"
                      title="Remove from disallowed list"
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < emails.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Info Box */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Users on this list will be immediately blocked from logging in.
          They will see an "Access denied" message when attempting to sign in.
        </Typography>
      </Alert>
    </Box>
  );
}

export default DisallowedList;
